import { findCatalogPlan } from '../views/Subscription/publicPlans.adapter'

export const PLAN_MODULE_KEYS = [
  'products',
  'warehouses',
  'stock_receipts',
  'inventory_adjustments',
  'sales',
  'estimates',
  'work_orders',
  'customers',
  'vendors',
  'purchase_orders',
  'expenses',
  'user_management',
  'settings',
  'reports',
  'pos',
  'cash_counter',
]

/** Legacy `module_in_package[].module_name` → commercial plan module key (GET /api/public/plans). */
export const PACKAGE_MODULE_NAME_TO_PLAN_KEY = {
  userManagment: 'user_management',
  products: 'products',
  warehouse: 'warehouses',
  vendor: 'vendors',
  stock_receipt: 'stock_receipts',
  stock_transfer: 'stock_transfers',
  adjustment: 'inventory_adjustments',
  customer: 'customers',
  sales: 'sales',
  expensive: 'expenses',
  configuration: 'settings',
  Reports: 'reports',
  reports: 'reports',
  pos: 'pos',
  purchase_order: 'purchase_orders',
}

/** Sidebar display tokens (Aside.jsx) from plan module key. */
export const PLAN_KEY_TO_SIDEBAR_TOKEN = {
  user_management: 'User Management',
  products: 'Products',
  warehouses: 'WareHouse',
  vendors: 'Vendor',
  stock_receipts: 'Stock Receipt',
  stock_transfers: 'Stock Transfer',
  inventory_adjustments: 'Adjustment',
  customers: 'Costumers',
  sales: 'Sales',
  expenses: 'Expensive',
  settings: 'Configuration',
  reports: 'Reports',
  pos: 'pos',
  purchase_orders: 'Purchase Order',
}

export function normalizePackageModuleName(moduleName) {
  return String(moduleName || '').trim()
}

export function planKeyFromPackageModuleName(moduleName) {
  const normalized = normalizePackageModuleName(moduleName)
  return PACKAGE_MODULE_NAME_TO_PLAN_KEY[normalized] || null
}

export function getCompanyPlanCode(userInfo) {
  const company = userInfo?.company || {}
  return String(company.package_code || company.packageCode || company.package_kind || company.packageKind || company.package?.code || company.package?.kind || '').toLowerCase()
}

export function getPackageModuleNames(userInfo) {
  const modules = userInfo?.company?.package?.module_in_package || []
  return modules.map((row) => normalizePackageModuleName(row?.module_name)).filter(Boolean)
}

export function companyHasPackageModuleName(userInfo, moduleName) {
  const needle = normalizePackageModuleName(moduleName)
  if (!needle) return false
  return getPackageModuleNames(userInfo).includes(needle)
}

export function getPackageAssignedPlanKeys(userInfo) {
  const keys = new Set()
  getPackageModuleNames(userInfo).forEach((name) => {
    const key = planKeyFromPackageModuleName(name)
    if (key) keys.add(key)
  })
  return keys
}

export function getCatalogPlanModuleKeys(userInfo, catalogPlans = []) {
  const code = getCompanyPlanCode(userInfo)
  if (!code || !catalogPlans.length) return new Set()
  const plan = findCatalogPlan(catalogPlans, code)
  if (!plan) return new Set()
  return new Set(Array.isArray(plan.modules) ? plan.modules : [])
}

export function isCatalogAvailable(catalogPlans, fetchError) {
  return !fetchError && Array.isArray(catalogPlans) && catalogPlans.length > 0
}

export function companyHasStockTransfersAccess(userInfo, catalogPlans = [], options = {}) {
  const { catalogAvailable = isCatalogAvailable(catalogPlans, options.fetchError) } = options
  if (companyHasPackageModuleName(userInfo, 'stock_transfer')) return true
  return companyHasPlanModuleAccess(userInfo, catalogPlans, 'stock_receipts', { catalogAvailable, fetchError: options.fetchError })
}

export function companyHasVendorBillsAccess(userInfo, catalogPlans = [], options = {}) {
  return companyHasPlanModuleAccess(userInfo, catalogPlans, 'purchase_orders', options) || companyHasPlanModuleAccess(userInfo, catalogPlans, 'expenses', options)
}

export function companyHasPlanModuleAccess(userInfo, catalogPlans = [], moduleKey, options = {}) {
  const key = String(moduleKey || '').trim()
  if (!key) return false
  if (key === 'stock_transfers') {
    return companyHasStockTransfersAccess(userInfo, catalogPlans, options)
  }
  if (getPackageAssignedPlanKeys(userInfo).has(key)) return true
  const catalogAvailable = options.catalogAvailable ?? isCatalogAvailable(catalogPlans, options.fetchError)
  if (catalogAvailable) {
    const catalogKeys = getCatalogPlanModuleKeys(userInfo, catalogPlans)
    if (catalogKeys.has(key)) return true
  }
  return false
}

export function companyHasAnyPlanModuleAccess(userInfo, catalogPlans = [], moduleKeys = [], options = {}) {
  const list = Array.isArray(moduleKeys) ? moduleKeys : [moduleKeys]
  return list.some((moduleKey) => companyHasPlanModuleAccess(userInfo, catalogPlans, moduleKey, options))
}

export function companyHasLegacyModuleNames(userInfo, legacyModuleNames = []) {
  const names = Array.isArray(legacyModuleNames) ? legacyModuleNames : [legacyModuleNames]
  return names.some((name) => companyHasPackageModuleName(userInfo, name))
}
